import { ApplicationType } from '@/generated/graphql';
import { CURRENCY_SYMBOLS } from '@/lib/utils/const';
import { format } from 'date-fns';
import type { Option } from '@/components/ui/DropDown';

//////////////////////////////////////////////////
//Currency Helpers
//////////////////////////////////////////////////

export const formatCurrency = (code?: string, format: 'code' | 'symbol' | 'codeAndSymbol' = 'symbol'): string => {
  if (!code) return '-';

  const symbol = CURRENCY_SYMBOLS[code] ?? code;

  switch (format) {
    case 'code':
      return code;
    case 'symbol':
      return symbol;
    case 'codeAndSymbol':
      return symbol && symbol !== code ? `${code} (${symbol})` : code;
  }
};

export const formatAmountAndCurrency = (currency?: string, amount?: string | number) => {
  if (amount == null) {
    return '-';
  }

  const numValue = typeof amount === 'string' ? Number(amount) : amount;
  if (Number.isNaN(numValue)) {
    return '-';
  }

  const formattedNumber = numValue.toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  if (currency == null) {
    return formattedNumber;
  }

  const symbol = CURRENCY_SYMBOLS[currency] ?? currency;
  const separator = symbol.length > 2 ? ' ' : '';
  return `${symbol}${separator}${formattedNumber}`;
};

//////////////////////////////////////////////////
//Formatting Helpers
//////////////////////////////////////////////////

export const getFullName = (firstName?: string, middleName?: string, lastName?: string): string => {
  const fullName = [firstName, middleName, lastName].filter(Boolean).join(' ').trim();

  return fullName || '-';
};

export const formatDate = (date?: Date | null | string) => {
  if (date == null) {
    return '-';
  }

  const parsedDate = new Date(date);

  return Number.isNaN(parsedDate.getTime()) ? '-' : format(parsedDate, 'dd-MM-yyyy');
};

export const formatDateTime = (date?: Date | null | string | number) => {
  if (date == null) return '-';

  const parsed =
    typeof date === 'number'
      ? new Date(date)
      : typeof date === 'string' && /^\d+$/.test(date)
        ? new Date(Number(date))
        : new Date(date);

  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  return format(parsed, 'dd-MM-yyyy HH:mm');
};

export const convertEnumToReadableString = (enumValue: string | undefined): string => {
  if (!enumValue) {
    return '';
  }

  return enumValue
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export function getOptionLabel(options: Option[], value?: string | string[]): string | string[] {
  if (!value || (Array.isArray(value) && value.length === 0)) return '-';

  if (Array.isArray(value)) {
    return value.map((v) => {
      const match = options.find((opt) => opt.value === v);
      return match ? match.label : v;
    });
  }

  const match = options.find((opt) => opt.value === value);
  return match ? match.label : value;
}

export const dateNotInPast = (date: Date | null) => {
  if (date == null) {
    return false;
  }

  const selectedDate = new Date(date);
  const today = new Date();

  today.setHours(0, 0, 0, 0);

  return selectedDate >= today;
};

//NOTE: this fn is currently not used
export const calculateAge = (birthDate: string) => {
  const birthDateParsed = new Date(birthDate);

  if (Number.isNaN(birthDateParsed.getTime())) {
    return undefined;
  }

  const today = new Date();
  let age = today.getFullYear() - birthDateParsed.getFullYear();
  const month = today.getMonth() - birthDateParsed.getMonth();

  if (month < 0 || (month === 0 && today.getDate() < birthDateParsed.getDate())) {
    age--;
  }

  return age;
};

////////////////////////////////////////////////////////////////////////////////////////////////////
//Application Helpers
////////////////////////////////////////////////////////////////////////////////////////////////////

export const getApplicationTypeDisplayName = (type: ApplicationType | null): string => {
  if (type == null) return '';

  // Custom display names mapping - temporary solution until SF decide on final naming conventions
  const customNames: Record<ApplicationType, string> = {
    [ApplicationType.SellOnFeesFinancing]: 'Sell-On Fees Financing',
    [ApplicationType.PlayerTransferFinancing]: 'Transfer Factoring',
    [ApplicationType.PayableFinancing]: 'Payables Financing',
    [ApplicationType.BridgeLoanFinancing]: 'Bridge Loan Financing',
    [ApplicationType.GeneralApplicationFinancing]: 'General Application Financing',
    [ApplicationType.GeneralEnquiryFinancing]: 'General Enquiry Financing',
    [ApplicationType.MediaRightsFinancing]: 'Media Rights Factoring',
    [ApplicationType.TermLoanFinancing]: 'Term Loan Financing',
  };

  if (type in customNames) {
    return customNames[type];
  }

  // Fallback for any future types
  const strippedType = type.replace(/_FINANCING$/, '');
  return convertEnumToReadableString(strippedType);
};

// Application type translation keys (must match keys in ApplicationTypeLabels)
export type ApplicationTypeTranslationKey =
  | 'SellOnFees'
  | 'PlayerTransfer'
  | 'Payables'
  | 'BridgeLoan'
  | 'GeneralApplication'
  | 'GeneralEnquiry'
  | 'MediaRights'
  | 'TermLoan';

const applicationTypeTranslationKeys: Record<ApplicationType, ApplicationTypeTranslationKey> = {
  [ApplicationType.SellOnFeesFinancing]: 'SellOnFees',
  [ApplicationType.PlayerTransferFinancing]: 'PlayerTransfer',
  [ApplicationType.PayableFinancing]: 'Payables',
  [ApplicationType.BridgeLoanFinancing]: 'BridgeLoan',
  [ApplicationType.GeneralApplicationFinancing]: 'GeneralApplication',
  [ApplicationType.GeneralEnquiryFinancing]: 'GeneralEnquiry',
  [ApplicationType.MediaRightsFinancing]: 'MediaRights',
  [ApplicationType.TermLoanFinancing]: 'TermLoan',
};

// Internationalized version of getApplicationTypeDisplayName
export const getTranslatedApplicationTypeDisplayName = (
  type: ApplicationType | null,
  translate: (key: ApplicationTypeTranslationKey) => string,
): string => {
  if (type == null) return '';

  if (type in applicationTypeTranslationKeys) {
    return translate(applicationTypeTranslationKeys[type]);
  }

  // Fallback for any future types
  const strippedType = type.replace(/_FINANCING$/, '');
  return convertEnumToReadableString(strippedType);
};

///////////////////////////////////////////////////////////////////////////////////
// Helper function to check if a clause is deeply empty - specifically for contracts
///////////////////////////////////////////////////////////////////////////////////

export const isClauseDeepEmpty = (value: unknown): boolean => {
  if (value == null || (typeof value === 'string' && value.trim() === '')) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.every(isClauseDeepEmpty);
  }

  if (typeof value === 'object') {
    return Object.values(value).every(isClauseDeepEmpty);
  }

  return false;
};
